import React from 'react';
import formatMoney from '@/lib/formatMoney';

const Stat = ({ label, value, valueClass }) => (
  <div className="flex flex-col">
    <span className="text-xs text-muted-foreground/60 leading-tight">{label}</span>
    <span className={`text-sm font-semibold leading-tight ${valueClass || 'text-foreground'}`}>{value}</span>
  </div>
);

const Sep = () => <div className="w-px h-8 bg-border/30 self-center" />;

export default function BacktestStats({ metrics, initialCapital, symbol }) {
  if (!metrics) return null;

  const retClass = metrics.totalReturn >= 0 ? 'text-green-400' : 'text-red-400';
  const bhClass = metrics.buyAndHoldReturn >= 0 ? 'text-green-400' : 'text-red-400';
  const alpha = metrics.totalReturn - metrics.buyAndHoldReturn;
  const alphaClass = alpha >= 0 ? 'text-green-400' : 'text-red-400';
  const eqClass = metrics.finalEquity >= initialCapital ? 'text-green-400' : 'text-red-400';

  return (
    <div className="flex items-center gap-4 flex-wrap">
      {symbol && (
        <>
          <span className="text-sm font-bold text-foreground tracking-wide">{symbol}</span>
          <Sep />
        </>
      )}
      <Stat label="Return" value={`${metrics.totalReturn?.toFixed(2)}%`} valueClass={retClass} />
      <Stat label="Final Equity" value={formatMoney(metrics.finalEquity)} valueClass={eqClass} />
      <Sep />
      <Stat label="Trades" value={metrics.totalTrades} />
      <Stat label="W / L" value={`${metrics.winningTrades} / ${metrics.losingTrades}`} />
      <Stat label="Win Rate" value={`${metrics.winRate?.toFixed(1)}%`} />
      <Sep />
      <Stat label="Sharpe" value={metrics.sharpeRatio?.toFixed(2)} valueClass={metrics.sharpeRatio >= 1 ? 'text-green-400' : 'text-yellow-400'} />
      <Stat label="Max Drawdown" value={`${metrics.maxDrawdown?.toFixed(2)}%`} valueClass="text-red-400" />
      <Stat label="Profit Factor" value={metrics.profitFactor} />
      <Sep />
      <Stat label="Avg Win" value={formatMoney(metrics.avgWin)} valueClass="text-green-400" />
      <Stat label="Avg Loss" value={formatMoney(metrics.avgLoss, { abs: true })} valueClass="text-red-400" />
      <Sep />
      <Stat label="Fees" value={formatMoney(metrics.totalTradeCosts || 0)} valueClass="text-orange-400" />
      <Stat label="Maker" value={formatMoney(metrics.totalMakerFees || 0)} valueClass="text-orange-400/70" />
      <Stat label="Taker" value={formatMoney(metrics.totalTakerFees || 0)} valueClass="text-orange-400/70" />
      <Sep />
      <Stat label="Buy & Hold" value={`${metrics.buyAndHoldReturn?.toFixed(2)}%`} valueClass={bhClass} />
      <Stat label="Alpha" value={`${alpha?.toFixed(2)}%`} valueClass={alphaClass} />
      {metrics.rebalanceCount > 0 && (
        <>
          <Sep />
          <Stat label="Rebalances" value={metrics.rebalanceCount} valueClass="text-blue-400" />
        </>
      )}
    </div>
  );
}
